// Adjuntos de un correo recibido: el servicio de correo los trae del buzon al abrir el hilo (el motor no los guarda).
// Imagenes en miniatura (se abren en grande con un clic); los demas archivos se descargan.
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { type AdjuntoRecibido, adjuntosDe, archivoAdjunto } from '../api/correo';
import { Icon } from '../lib/icons';
import { partesEnLinea } from '../lib/imagenesEnLinea';

const tamanoTexto = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
export const esImagen = (a: AdjuntoRecibido) => /^image\/(png|jpe?g|gif|webp)\s*(;|$)/i.test(a.tipo);

// texto: el correo completo; visible: lo que se muestra (sin firma ni citas). Una imagen nombrada solo en la parte que
// no se muestra es de la firma (D40).
export function AdjuntosCorreo({ mensajeId, texto = '', visible }: { mensajeId: string; texto?: string; visible?: string }) {
  const r = useQuery({ queryKey: ['adjuntos', mensajeId], queryFn: () => adjuntosDe(mensajeId), staleTime: 30 * 60_000, retry: 1 });
  if (r.isLoading) return null;
  if (r.error) return <p className="note">No se pudieron traer los adjuntos: {(r.error as Error).message}</p>;
  return <Adjuntos lista={r.data ?? []} traer={(a) => archivoAdjunto(mensajeId, a.parte)} clave={mensajeId} texto={texto} visible={visible} />;
}

// Lista de adjuntos de un mensaje, venga del buzon o de Zammad (D42): `traer` baja el archivo.
export function Adjuntos({ lista, traer, clave, texto = '', visible }: { lista: AdjuntoRecibido[]; traer: (a: AdjuntoRecibido) => Promise<Blob>; clave: string; texto?: string; visible?: string }) {
  const mensajeId = clave;
  if (!lista.length) return null;
  // Imagenes incrustadas que el texto no nombra ("[image: foto.png]") suelen ser logos de la firma: van plegadas.
  const nombres = (t: string) => new Set([...t.matchAll(/\[image:\s*([^\]]+?)\s*\]/gi)].map((m) => m[1].toLowerCase()));
  const nombradas = nombres(texto);
  const enVista = nombres(visible ?? texto);
  // Las que van dentro del texto ya se ven ahi (TextoConImagenes); las demas, en el orden del correo.
  const dentro = partesEnLinea(visible ?? texto, lista.filter(esImagen));
  const imagenes = lista.filter((a) => esImagen(a) && !dentro.has(a.parte));
  const firma = imagenes.filter((a) => a.enLinea && (nombradas.size ? !enVista.has(a.nombre.toLowerCase()) : a.tamano < 15_000));
  const grandes = imagenes.filter((a) => !firma.includes(a));
  if (!imagenes.length && lista.every(esImagen)) return null;
  return (
    <div className="adjuntos-recibidos">
      {grandes.map((a) => (
        <Miniatura key={a.parte} mensajeId={mensajeId} a={a} traer={traer} />
      ))}
      {firma.length ? <Firma mensajeId={mensajeId} imagenes={firma} traer={traer} /> : null}
      {lista
        .filter((a) => !esImagen(a))
        .map((a) => (
          <button key={a.parte} type="button" className="adjunto" onClick={() => void abrir(traer, a)} title="Descargar">
            <Icon name="paperclip" size={13} /> {a.nombre} <small>{tamanoTexto(a.tamano)}</small>
          </button>
        ))}
    </div>
  );
}

function Firma({ mensajeId, imagenes, traer }: { mensajeId: string; imagenes: AdjuntoRecibido[]; traer: (a: AdjuntoRecibido) => Promise<Blob> }) {
  const [ver, setVer] = useState(false);
  return ver ? (
    <>
      {imagenes.map((a) => (
        <Miniatura key={a.parte} mensajeId={mensajeId} a={a} pequena traer={traer} />
      ))}
    </>
  ) : (
    <button type="button" className="linkish" onClick={() => setVer(true)}>
      {imagenes.length === 1 ? '1 imagen de la firma' : `${imagenes.length} imágenes de la firma`}
    </button>
  );
}

export function Miniatura({ mensajeId, a, pequena = false, enLinea = false, traer }: { mensajeId: string; a: AdjuntoRecibido; pequena?: boolean; enLinea?: boolean; traer: (a: AdjuntoRecibido) => Promise<Blob> }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    let hecha: string | null = null;
    traer(a)
      .then((b) => {
        hecha = URL.createObjectURL(b);
        if (vivo) setUrl(hecha);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
      if (hecha) URL.revokeObjectURL(hecha);
    };
  }, [mensajeId, a.parte]);
  return (
    <a className={`adjunto-img ${pequena ? 'pequena' : ''} ${enLinea ? 'en-linea' : ''}`} href={url ?? undefined} target="_blank" rel="noreferrer" title={`${a.nombre} · ${tamanoTexto(a.tamano)}`}>
      {url ? <img src={url} alt={a.nombre} /> : <span className="note">{a.nombre}</span>}
    </a>
  );
}

async function abrir(traer: (a: AdjuntoRecibido) => Promise<Blob>, a: AdjuntoRecibido) {
  const b = await traer(a);
  const url = URL.createObjectURL(b);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = a.nombre;
  enlace.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
